import { brandPalette } from "../../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  RiArrowDownLine,
  RiArrowUpLine,
  RiCropLine,
  RiDeleteBinLine,
  RiFileCopyLine,
  RiFileUploadLine,
  RiZoomInLine,
} from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import QuestionImageCropEditor from "../../../../components/admin/QuestionImageCropEditor";
import MainModel from "../../../../components/Models/main-model";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
} from "../../../../components/Models/admin-modal-theme";
import { getAllChaptersRequest } from "../../../../modules/admin-modules/chapters/Actions";
import { closeBulkImportQuestionsModel } from "../../../../modules/admin-modules/models/Actions";
import {
  bulkCreateQuestionBankRequest,
  splitPdfQuestionsFail,
  splitPdfQuestionsRequest,
} from "../../../../modules/admin-modules/questions-bank/Actions";
import { getAllTagsRequest } from "../../../../modules/admin-modules/tags/Actions";

const difficultyOptions = [
  { value: "1", label: "Easy" },
  { value: "2", label: "Medium" },
  { value: "3", label: "Hard" },
];

const ASSET_ACCEPT = "image/*,application/pdf,.pdf";
const PDF_ACCEPT = "application/pdf,.pdf";

const makeDraftFromSplit = (questions = []) =>
  questions.map((q, idx) => ({
    key: `${q.page}-${q.index}-${idx}`,
    page: q.page,
    index: q.index,
    image_base64: q.image_base64,
    original_image_base64: q.image_base64,
    mime_type: q.mime_type || "image/jpeg",
    question: `Q${idx + 1}`,
    mark: 1,
    difficulty_level: "2",
    selectedTags: [],
    url: "",
    pdf_base64: null,
    pdf_mime_type: null,
    pdf_file_name: "",
    model_answer_image_base64: null,
    model_answer_mime_type: null,
    model_answer_file_name: "",
    cambridge_image_base64: null,
    cambridge_mime_type: null,
    cambridge_file_name: "",
  }));

const makeSplitDrafts = (questions = [], prefix) =>
  questions.map((q, idx) => ({
    key: `${prefix}-${q.page}-${q.index}-${idx}`,
    page: q.page,
    index: q.index,
    image_base64: q.image_base64,
    original_image_base64: q.image_base64,
    mime_type: q.mime_type || "image/jpeg",
  }));

const renumberDraftQuestions = (list) =>
  list.map((item, idx) => ({ ...item, question: `Q${idx + 1}` }));

const imageDataUrl = (item) => `data:${item.mime_type};base64,${item.image_base64}`;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve({
        base64,
        mime_type: file.type || "application/octet-stream",
        name: file.name,
      });
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const loadHtmlImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });

const mergeImagesVertically = async (topItem, bottomItem) => {
  const [topImg, bottomImg] = await Promise.all([
    loadHtmlImage(imageDataUrl(topItem)),
    loadHtmlImage(imageDataUrl(bottomItem)),
  ]);
  const width = Math.max(topImg.naturalWidth, bottomImg.naturalWidth);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = topImg.naturalHeight + bottomImg.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(topImg, Math.round((width - topImg.naturalWidth) / 2), 0);
  ctx.drawImage(
    bottomImg,
    Math.round((width - bottomImg.naturalWidth) / 2),
    topImg.naturalHeight
  );
  const mime_type = "image/jpeg";
  const dataUrl = canvas.toDataURL(mime_type, 0.92);
  return {
    image_base64: dataUrl.split(",")[1],
    mime_type,
  };
};

const outlineBtnProps = {
  size: "sm",
  variant: "outline",
  borderColor: brandPalette.primary,
  color: brandPalette.primary,
  fontWeight: "700",
};

const FilePickButton = ({ label, accept, onFile, ...props }) => (
  <Button
    size="sm"
    bg={brandPalette.primary}
    color="white"
    borderRadius="12px"
    position="relative"
    fontWeight="700"
    _hover={{ bg: brandPalette.accentDark }}
    {...props}
  >
    {label}
    <Input
      type="file"
      accept={accept}
      opacity="0"
      position="absolute"
      w="100%"
      h="100%"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) onFile(file);
        e.target.value = "";
      }}
    />
  </Button>
);

const SplitUploadBox = ({
  title,
  file,
  onFile,
  pages,
  onPages,
  onSplit,
  isLoading,
  buttonLabel,
}) => (
  <Box
    p={4}
    borderWidth="1px"
    borderColor="var(--border-subtle)"
    borderRadius="12px"
    bg="var(--surface-muted)"
  >
    <Text fontWeight="700" color="var(--text-primary)" mb={3}>
      {title}
    </Text>
    <VStack align="stretch" spacing={3}>
      <Button
        leftIcon={<RiFileUploadLine />}
        bg={brandPalette.primary}
        color="white"
        borderRadius="12px"
        position="relative"
        _hover={{ bg: brandPalette.accentDark }}
      >
        {file ? file.name : "Choose file"}
        <Input
          type="file"
          accept="application/pdf,image/*,.pdf"
          opacity="0"
          position="absolute"
          w="100%"
          h="100%"
          onChange={(e) => onFile(e.target.files?.[0] || null)}
        />
      </Button>
      <Input
        value={pages}
        onChange={(e) => onPages(e.target.value)}
        placeholder="Pages (optional, e.g. 1-3)"
        bg="var(--surface-raised)"
        borderRadius="12px"
        borderColor="var(--border-subtle)"
      />
      <Button
        bg={brandPalette.primary}
        color="white"
        borderRadius="lg"
        fontWeight="800"
        isLoading={isLoading}
        onClick={onSplit}
        _hover={{ bg: brandPalette.accentDark }}
      >
        {buttonLabel}
      </Button>
    </VStack>
  </Box>
);

const ImageWorkbench = ({
  item,
  alt,
  canMergeAbove,
  canMergeBelow,
  merging,
  onView,
  onCrop,
  onResetCrop,
  onMergeAbove,
  onMergeBelow,
}) => (
  <Box
    borderWidth="1px"
    borderColor="var(--border-subtle)"
    borderRadius="12px"
    overflow="hidden"
    bg="var(--surface-raised)"
    p={2}
  >
    <Box
      as="button"
      type="button"
      onClick={onView}
      w="100%"
      borderRadius="8px"
      overflow="hidden"
      cursor="pointer"
      _hover={{ opacity: 0.92 }}
    >
      <Image
        src={imageDataUrl(item)}
        alt={alt}
        maxH="220px"
        objectFit="contain"
        mx="auto"
        display="block"
      />
    </Box>
    <HStack mt={2} spacing={2} flexWrap="wrap">
      <Button {...outlineBtnProps} leftIcon={<RiZoomInLine />} onClick={onView}>
        View
      </Button>
      <Button {...outlineBtnProps} leftIcon={<RiCropLine />} onClick={onCrop}>
        Crop
      </Button>
      <Button
        {...outlineBtnProps}
        leftIcon={<RiArrowUpLine />}
        isDisabled={!canMergeAbove || merging}
        onClick={onMergeAbove}
      >
        Merge with above
      </Button>
      <Button
        {...outlineBtnProps}
        leftIcon={<RiArrowDownLine />}
        isDisabled={!canMergeBelow || merging}
        onClick={onMergeBelow}
      >
        Merge with below
      </Button>
      {item.original_image_base64 && item.image_base64 !== item.original_image_base64 ? (
        <Button size="sm" variant="ghost" fontWeight="700" onClick={onResetCrop}>
          Reset crop
        </Button>
      ) : null}
    </HStack>
  </Box>
);

const BulkImportQuestions = () => {
  const dispatch = useDispatch();
  const openModal = useSelector((state) => state.model.isOpen45);
  const chapters = useSelector((state) => state.chapters.chapters);
  const tags = useSelector((state) => state.adminTags.tags);
  const isLoading = useSelector((state) => state.questionsBank.isLoading);
  const splitLoading = useSelector((state) => state.questionsBank.splitLoading);
  const splitLoadingTarget = useSelector((state) => state.questionsBank.splitLoadingTarget);
  const splitResult = useSelector((state) => state.questionsBank.splitResult);
  const splitTarget = useSelector((state) => state.questionsBank.splitTarget);

  const [selectedFile, setSelectedFile] = useState(null);
  const [pages, setPages] = useState("");
  const [modelAnswerFile, setModelAnswerFile] = useState(null);
  const [modelAnswerPages, setModelAnswerPages] = useState("");
  const [cambridgeFile, setCambridgeFile] = useState(null);
  const [cambridgePages, setCambridgePages] = useState("");
  const [chapterId, setChapterId] = useState("");
  const [draftQuestions, setDraftQuestions] = useState([]);
  const [modelAnswerDrafts, setModelAnswerDrafts] = useState([]);
  const [cambridgeDrafts, setCambridgeDrafts] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);
  const [cropTarget, setCropTarget] = useState(null);
  const [mergingKey, setMergingKey] = useState(null);
  const mergingRef = useRef(false);

  const selectStyles = {
    ...adminSelectStyles(),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    menuList: (base) => ({
      ...base,
      maxHeight: "240px",
      backgroundColor: "var(--surface-raised)",
    }),
  };
  const selectMenuPortalProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
  };

  const chaptersOptions = useMemo(
    () =>
      (chapters || [])
        .filter((c) => Number(c?.active) === 0)
        .map((c) => ({ value: c.id, label: c.name })),
    [chapters]
  );

  const tagsOptions = useMemo(
    () => (tags || []).map((t) => ({ value: t.id, label: t.name })),
    [tags]
  );

  useEffect(() => {
    if (openModal) {
      dispatch(getAllChaptersRequest());
      dispatch(getAllTagsRequest());
    }
  }, [openModal, dispatch]);

  useEffect(() => {
    if (!splitResult?.questions?.length) return;
    if (splitTarget === "model_answers") {
      setModelAnswerDrafts(makeSplitDrafts(splitResult.questions, "ma"));
      setCropTarget((prev) => (prev?.list === "model_answers" ? null : prev));
      setPreviewItem((prev) => (prev?.list === "model_answers" ? null : prev));
      return;
    }
    if (splitTarget === "cambridge") {
      setCambridgeDrafts(makeSplitDrafts(splitResult.questions, "ms"));
      setCropTarget((prev) => (prev?.list === "cambridge" ? null : prev));
      setPreviewItem((prev) => (prev?.list === "cambridge" ? null : prev));
      return;
    }
    setDraftQuestions(makeDraftFromSplit(splitResult.questions));
    setCropTarget((prev) => (prev?.list === "questions" ? null : prev));
    setPreviewItem((prev) => (prev?.list === "questions" ? null : prev));
  }, [splitResult, splitTarget]);

  const resetForm = () => {
    setSelectedFile(null);
    setPages("");
    setModelAnswerFile(null);
    setModelAnswerPages("");
    setCambridgeFile(null);
    setCambridgePages("");
    setChapterId("");
    setDraftQuestions([]);
    setModelAnswerDrafts([]);
    setCambridgeDrafts([]);
    setPreviewItem(null);
    setCropTarget(null);
    setMergingKey(null);
  };

  const closeFunc = () => {
    dispatch(splitPdfQuestionsFail());
    dispatch(closeBulkImportQuestionsModel());
    resetForm();
  };

  const handleSplit = (kind) => {
    const source =
      kind === "model_answers"
        ? { file: modelAnswerFile, pageRange: modelAnswerPages }
        : kind === "cambridge"
          ? { file: cambridgeFile, pageRange: cambridgePages }
          : { file: selectedFile, pageRange: pages };
    if (!source.file) {
      toast.error("Choose a PDF or image file first.");
      return;
    }
    const formData = new FormData();
    formData.append("file", source.file);
    if (source.pageRange.trim()) formData.append("pages", source.pageRange.trim());
    dispatch(splitPdfQuestionsRequest(formData, kind));
  };

  const draftsFor = (list) => {
    if (list === "model_answers") return modelAnswerDrafts;
    if (list === "cambridge") return cambridgeDrafts;
    return draftQuestions;
  };

  const patchDrafts = (list, updater) => {
    if (list === "model_answers") setModelAnswerDrafts(updater);
    else if (list === "cambridge") setCambridgeDrafts(updater);
    else setDraftQuestions(updater);
  };

  const updateDraft = (key, patch) => {
    setDraftQuestions((prev) =>
      prev.map((item) => (item.key === key ? { ...item, ...patch } : item))
    );
  };

  const clearOpenEditors = (key) => {
    if (previewItem?.key === key) setPreviewItem(null);
    if (cropTarget?.item?.key === key) setCropTarget(null);
  };

  const removeFromList = (list, key) => {
    patchDrafts(list, (prev) => {
      const next = prev.filter((item) => item.key !== key);
      return list === "questions" ? renumberDraftQuestions(next) : next;
    });
    clearOpenEditors(key);
  };

  const duplicateInList = (list, key) => {
    patchDrafts(list, (prev) => {
      const index = prev.findIndex((item) => item.key === key);
      if (index < 0) return prev;
      const source = prev[index];
      const copy = {
        ...source,
        key: `${source.key}-copy-${Date.now()}`,
        selectedTags: source.selectedTags ? [...source.selectedTags] : undefined,
      };
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return list === "questions" ? renumberDraftQuestions(next) : next;
    });
  };

  const mergeInList = async (list, key, direction) => {
    if (mergingRef.current) return;
    const items = draftsFor(list);
    const index = items.findIndex((item) => item.key === key);
    if (index < 0) return;
    const otherIndex = direction === "below" ? index + 1 : index - 1;
    if (otherIndex < 0 || otherIndex >= items.length) return;

    const top = direction === "below" ? items[index] : items[otherIndex];
    const bottom = direction === "below" ? items[otherIndex] : items[index];
    const survivor = direction === "below" ? items[index] : items[otherIndex];
    const removed = direction === "below" ? items[otherIndex] : items[index];

    mergingRef.current = true;
    setMergingKey(key);
    try {
      const merged = await mergeImagesVertically(top, bottom);
      const patch = {
        image_base64: merged.image_base64,
        original_image_base64: merged.image_base64,
        mime_type: merged.mime_type,
      };
      patchDrafts(list, (prev) => {
        const next = prev
          .map((item) => (item.key === survivor.key ? { ...item, ...patch } : item))
          .filter((item) => item.key !== removed.key);
        return list === "questions" ? renumberDraftQuestions(next) : next;
      });
      clearOpenEditors(removed.key);
      clearOpenEditors(survivor.key);
      toast.success("Merged into one image. Duplicate will keep the merged photo.");
    } catch (err) {
      toast.error("Failed to merge images.");
    } finally {
      mergingRef.current = false;
      setMergingKey(null);
    }
  };

  const handleHintUpload = async (key, file) => {
    if (!file) return;
    try {
      const { base64, mime_type, name } = await fileToBase64(file);
      updateDraft(key, {
        model_answer_image_base64: base64,
        model_answer_mime_type: mime_type,
        model_answer_file_name: name,
      });
    } catch (err) {
      toast.error("Failed to read hint file.");
    }
  };

  const clearHint = (key) => {
    updateDraft(key, {
      model_answer_image_base64: null,
      model_answer_mime_type: null,
      model_answer_file_name: "",
    });
  };

  const handlePdfUpload = async (key, file) => {
    if (!file) return;
    const isPdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Please choose a PDF file.");
      return;
    }
    try {
      const { base64, mime_type, name } = await fileToBase64(file);
      updateDraft(key, {
        pdf_base64: base64,
        pdf_mime_type: mime_type || "application/pdf",
        pdf_file_name: name,
      });
    } catch (err) {
      toast.error("Failed to read PDF file.");
    }
  };

  const clearPdf = (key) => {
    updateDraft(key, {
      pdf_base64: null,
      pdf_mime_type: null,
      pdf_file_name: "",
    });
  };

  const handleCambridgeUpload = async (key, file) => {
    if (!file) return;
    try {
      const { base64, mime_type, name } = await fileToBase64(file);
      updateDraft(key, {
        cambridge_image_base64: base64,
        cambridge_mime_type: mime_type,
        cambridge_file_name: name,
      });
    } catch (err) {
      toast.error("Failed to read mark scheme file.");
    }
  };

  const clearCambridge = (key) => {
    updateDraft(key, {
      cambridge_image_base64: null,
      cambridge_mime_type: null,
      cambridge_file_name: "",
    });
  };

  const applyCrop = (cropped) => {
    if (!cropTarget?.item) return;
    const patch = {
      image_base64: cropped.image_base64,
      mime_type: cropped.mime_type,
    };
    if (cropTarget.list === "questions") {
      updateDraft(cropTarget.item.key, patch);
      return;
    }
    patchDrafts(cropTarget.list, (prev) =>
      prev.map((item) => (item.key === cropTarget.item.key ? { ...item, ...patch } : item))
    );
  };

  const resetCrop = (list, key) => {
    const item = draftsFor(list).find((q) => q.key === key);
    if (!item?.original_image_base64) return;
    const patch = {
      image_base64: item.original_image_base64,
      mime_type: item.mime_type || "image/jpeg",
    };
    if (list === "questions") {
      updateDraft(key, patch);
      return;
    }
    patchDrafts(list, (prev) => prev.map((q) => (q.key === key ? { ...q, ...patch } : q)));
  };

  const handleImport = () => {
    if (!chapterId) {
      toast.error("Select a chapter.");
      return;
    }
    if (!draftQuestions.length) {
      toast.error("No questions to import.");
      return;
    }
    const mismatched = [];
    if (modelAnswerDrafts.length > 0 && modelAnswerDrafts.length !== draftQuestions.length) {
      mismatched.push(
        `${draftQuestions.length} questions vs ${modelAnswerDrafts.length} model answers`
      );
    }
    if (cambridgeDrafts.length > 0 && cambridgeDrafts.length !== draftQuestions.length) {
      mismatched.push(
        `${draftQuestions.length} questions vs ${cambridgeDrafts.length} mark schemes`
      );
    }
    if (mismatched.length) {
      toast.warn(`Pairing by row: ${mismatched.join("; ")}. Extra images on either side are skipped.`);
    }

    const payload = {
      chapter_id: chapterId,
      questions: draftQuestions.map((q, idx) => {
        const pairedAnswer = modelAnswerDrafts[idx];
        const pairedScheme = cambridgeDrafts[idx];
        const item = {
          question: q.question,
          mark: Number(q.mark) || 1,
          difficulty_level: Number(q.difficulty_level) || 2,
          tags: (q.selectedTags || []).map((t) => t.value),
          image_base64: q.image_base64,
          mime_type: q.mime_type,
        };
        if (q.url?.trim()) item.url = q.url.trim();
        if (q.pdf_base64) {
          item.pdf_base64 = q.pdf_base64;
          item.pdf_mime_type = q.pdf_mime_type || "application/pdf";
        }
        if (q.model_answer_image_base64) {
          item.model_answer_image_base64 = q.model_answer_image_base64;
          item.model_answer_mime_type = q.model_answer_mime_type || "image/jpeg";
        } else if (pairedAnswer?.image_base64) {
          item.model_answer_image_base64 = pairedAnswer.image_base64;
          item.model_answer_mime_type = pairedAnswer.mime_type || "image/jpeg";
        }
        if (q.cambridge_image_base64) {
          item.cambridge_image_base64 = q.cambridge_image_base64;
          item.cambridge_mime_type = q.cambridge_mime_type || "image/jpeg";
        } else if (pairedScheme?.image_base64) {
          item.cambridge_image_base64 = pairedScheme.image_base64;
          item.cambridge_mime_type = pairedScheme.mime_type || "image/jpeg";
        }
        return item;
      }),
    };

    dispatch(
      bulkCreateQuestionBankRequest(payload, () => {
        closeFunc();
      })
    );
  };

  const renderListActions = (list, item) => (
    <HStack spacing={2}>
      <Button
        {...outlineBtnProps}
        leftIcon={<RiFileCopyLine />}
        onClick={() => duplicateInList(list, item.key)}
      >
        Duplicate
      </Button>
      <Button
        size="sm"
        leftIcon={<RiDeleteBinLine />}
        variant="outline"
        borderColor="red.400"
        color="red.500"
        fontWeight="700"
        onClick={() => removeFromList(list, item.key)}
      >
        Delete
      </Button>
    </HStack>
  );

  const renderQuestionCard = (item, idx) => (
    <Box
      p={4}
      borderWidth="1px"
      borderColor="var(--border-subtle)"
      borderRadius="12px"
      bg="var(--surface-muted)"
      h="100%"
    >
      <Flex justify="space-between" align="flex-start" gap={4} mb={3}>
        <Text fontWeight="800" color={brandPalette.primary}>
          Question {idx + 1}
          {item.page != null ? ` (page ${item.page})` : ""}
        </Text>
        {renderListActions("questions", item)}
      </Flex>
      <Stack spacing={3}>
        <ImageWorkbench
          item={item}
          alt={`Question ${idx + 1}`}
          canMergeAbove={idx > 0}
          canMergeBelow={idx < draftQuestions.length - 1}
          merging={Boolean(mergingKey)}
          onView={() => setPreviewItem({ ...item, list: "questions" })}
          onCrop={() => setCropTarget({ list: "questions", item })}
          onResetCrop={() => resetCrop("questions", item.key)}
          onMergeAbove={() => mergeInList("questions", item.key, "above")}
          onMergeBelow={() => mergeInList("questions", item.key, "below")}
        />
        <FormControl>
          <FormLabel m={0} mb={1} fontSize="14px" fontWeight="700">
            Question text
          </FormLabel>
          <Input
            value={item.question}
            onChange={(e) => updateDraft(item.key, { question: e.target.value })}
            bg="var(--surface-raised)"
            borderRadius="12px"
          />
        </FormControl>
        <FormControl>
          <FormLabel m={0} mb={1} fontSize="14px" fontWeight="700">
            Marks
          </FormLabel>
          <Input
            type="number"
            min={1}
            value={item.mark}
            onChange={(e) => updateDraft(item.key, { mark: e.target.value })}
            bg="var(--surface-raised)"
            borderRadius="12px"
          />
        </FormControl>
        <FormControl>
          <FormLabel m={0} mb={1} fontSize="14px" fontWeight="700">
            Difficulty
          </FormLabel>
          <HStack spacing={4} flexWrap="wrap">
            {difficultyOptions.map((opt) => (
              <label key={opt.value}>
                <input
                  type="radio"
                  name={`difficulty-${item.key}`}
                  value={opt.value}
                  checked={String(item.difficulty_level) === opt.value}
                  onChange={() => updateDraft(item.key, { difficulty_level: opt.value })}
                />{" "}
                {opt.label}
              </label>
            ))}
          </HStack>
        </FormControl>
        <FormControl>
          <FormLabel m={0} mb={1} fontSize="14px" fontWeight="700">
            Video answer URL (optional)
          </FormLabel>
          <Input
            type="url"
            value={item.url || ""}
            onChange={(e) => updateDraft(item.key, { url: e.target.value })}
            placeholder="https://..."
            bg="var(--surface-raised)"
            borderRadius="12px"
          />
        </FormControl>
        <FormControl>
          <FormLabel m={0} mb={1} fontSize="14px" fontWeight="700">
            Question PDF (optional)
          </FormLabel>
          <HStack spacing={2} flexWrap="wrap">
            <FilePickButton
              label={item.pdf_file_name || "Choose PDF"}
              accept={PDF_ACCEPT}
              onFile={(file) => handlePdfUpload(item.key, file)}
            />
            {item.pdf_file_name ? (
              <Button size="sm" variant="ghost" fontWeight="700" onClick={() => clearPdf(item.key)}>
                Clear
              </Button>
            ) : null}
          </HStack>
        </FormControl>
        <FormControl>
          <FormLabel m={0} mb={1} fontSize="14px" fontWeight="700">
            Hint override (optional)
          </FormLabel>
          <HStack spacing={2} flexWrap="wrap">
            <FilePickButton
              label={item.model_answer_file_name || "Choose file"}
              accept={ASSET_ACCEPT}
              onFile={(file) => handleHintUpload(item.key, file)}
            />
            {item.model_answer_file_name ? (
              <Button size="sm" variant="ghost" fontWeight="700" onClick={() => clearHint(item.key)}>
                Clear
              </Button>
            ) : null}
          </HStack>
          <Text mt={1} fontSize="xs" color="var(--text-muted)">
            Use only if this question should not use the paired model-answer image.
          </Text>
        </FormControl>
        <FormControl>
          <FormLabel m={0} mb={1} fontSize="14px" fontWeight="700">
            Mark scheme override (optional)
          </FormLabel>
          <HStack spacing={2} flexWrap="wrap">
            <FilePickButton
              label={item.cambridge_file_name || "Choose file"}
              accept={ASSET_ACCEPT}
              onFile={(file) => handleCambridgeUpload(item.key, file)}
            />
            {item.cambridge_file_name ? (
              <Button
                size="sm"
                variant="ghost"
                fontWeight="700"
                onClick={() => clearCambridge(item.key)}
              >
                Clear
              </Button>
            ) : null}
          </HStack>
          <Text mt={1} fontSize="xs" color="var(--text-muted)">
            Use only if this question should not use the paired mark-scheme image.
          </Text>
        </FormControl>
        <FormControl>
          <FormLabel m={0} mb={1} fontSize="14px" fontWeight="700">
            Tags
          </FormLabel>
          <Select
            options={tagsOptions}
            isMulti
            value={item.selectedTags}
            onChange={(selected) => updateDraft(item.key, { selectedTags: selected || [] })}
            styles={selectStyles}
            placeholder="Tags"
            {...selectMenuPortalProps}
          />
        </FormControl>
      </Stack>
    </Box>
  );

  const renderSideCard = (item, idx, list) => {
    const drafts = draftsFor(list);
    const isCambridge = list === "cambridge";
    const title = isCambridge ? "Mark scheme" : "Model answer";
    return (
      <Box
        p={4}
        borderWidth="1px"
        borderColor="var(--border-subtle)"
        borderRadius="12px"
        bg="var(--surface-muted)"
        h="100%"
      >
        <Flex justify="space-between" align="flex-start" gap={4} mb={3}>
          <Text fontWeight="800" color={brandPalette.primary}>
            {title} {idx + 1}
            {item.page != null ? ` (page ${item.page})` : ""}
          </Text>
          {renderListActions(list, item)}
        </Flex>
        <ImageWorkbench
          item={item}
          alt={`${title} ${idx + 1}`}
          canMergeAbove={idx > 0}
          canMergeBelow={idx < drafts.length - 1}
          merging={Boolean(mergingKey)}
          onView={() => setPreviewItem({ ...item, list })}
          onCrop={() => setCropTarget({ list, item })}
          onResetCrop={() => resetCrop(list, item.key)}
          onMergeAbove={() => mergeInList(list, item.key, "above")}
          onMergeBelow={() => mergeInList(list, item.key, "below")}
        />
        <Text mt={3} fontSize="sm" color="var(--text-muted)">
          {isCambridge
            ? `Saved as the Cambridge mark scheme for question ${idx + 1}.`
            : `Saved as the handwritten / model answer for question ${idx + 1}.`}
        </Text>
      </Box>
    );
  };

  const emptyPane = (label) => (
    <Box
      p={6}
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="var(--border-subtle)"
      borderRadius="12px"
      bg="var(--surface-muted)"
      minH="160px"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Text color="var(--text-muted)" textAlign="center">
        {label}
      </Text>
    </Box>
  );

  const pairCount = Math.max(
    draftQuestions.length,
    modelAnswerDrafts.length,
    cambridgeDrafts.length
  );
  const showReview =
    draftQuestions.length > 0 || modelAnswerDrafts.length > 0 || cambridgeDrafts.length > 0;
  const countsDiffer =
    (modelAnswerDrafts.length > 0 && modelAnswerDrafts.length !== draftQuestions.length) ||
    (cambridgeDrafts.length > 0 && cambridgeDrafts.length !== draftQuestions.length);

  const header = (
    <Box>
      <Heading
        fontSize="32px"
        fontWeight="800"
        color="var(--text-primary)"
        fontFamily="'Amiri','Tajawal',sans-serif"
      >
        Import from PDF
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.14em"
        fontWeight="700"
      >
        Split questions, model answers, and mark schemes, then crop, merge, or duplicate before import
      </Text>
    </Box>
  );

  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openModal}
      buttonTitle={null}
      size="full"
      contentProps={{ ...adminModalContentProps, maxW: "96vw", w: "96vw" }}
      headerProps={adminModalHeaderProps}
      bodyProps={{ ...adminModalBodyProps, maxH: "80vh", overflowY: "auto" }}
      hideDefaultFooter
    >
      <Stack spacing={6} w="100%" dir="ltr">
        <Box>
          <Text fontWeight="700" color="var(--text-primary)" mb={3}>
            Step 1 — Upload & split
          </Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <SplitUploadBox
              title="Questions PDF"
              file={selectedFile}
              onFile={setSelectedFile}
              pages={pages}
              onPages={setPages}
              onSplit={() => handleSplit("questions")}
              isLoading={splitLoading && splitLoadingTarget === "questions"}
              buttonLabel="Split into questions"
            />
            <SplitUploadBox
              title="Model answer PDF"
              file={modelAnswerFile}
              onFile={setModelAnswerFile}
              pages={modelAnswerPages}
              onPages={setModelAnswerPages}
              onSplit={() => handleSplit("model_answers")}
              isLoading={splitLoading && splitLoadingTarget === "model_answers"}
              buttonLabel="Split into model answers"
            />
            <SplitUploadBox
              title="Mark scheme PDF"
              file={cambridgeFile}
              onFile={setCambridgeFile}
              pages={cambridgePages}
              onPages={setCambridgePages}
              onSplit={() => handleSplit("cambridge")}
              isLoading={splitLoading && splitLoadingTarget === "cambridge"}
              buttonLabel="Split into mark schemes"
            />
          </SimpleGrid>
        </Box>

        {showReview ? (
          <>
            <Box
              position="sticky"
              top={0}
              zIndex={2}
              p={4}
              borderWidth="1px"
              borderColor="var(--border-subtle)"
              borderRadius="12px"
              bg="var(--surface-raised)"
              boxShadow="sm"
            >
              <Text fontWeight="700" color="var(--text-primary)" mb={3}>
                Step 2 — Shared chapter
              </Text>
              <FormControl>
                <FormLabel m={0} mb={2} fontSize="14px" fontWeight="700" color="var(--text-primary)">
                  Chapter (required)
                </FormLabel>
                <Select
                  options={chaptersOptions}
                  value={chaptersOptions.find((o) => String(o.value) === String(chapterId)) || null}
                  onChange={(opt) => setChapterId(opt?.value ?? "")}
                  styles={selectStyles}
                  placeholder="Select chapter"
                  {...selectMenuPortalProps}
                />
              </FormControl>
              <Text mt={2} fontSize="sm" color="var(--text-muted)">
                {draftQuestions.length} question{draftQuestions.length === 1 ? "" : "s"}
                {modelAnswerDrafts.length
                  ? ` · ${modelAnswerDrafts.length} model answer${
                      modelAnswerDrafts.length === 1 ? "" : "s"
                    }`
                  : ""}
                {cambridgeDrafts.length
                  ? ` · ${cambridgeDrafts.length} mark scheme${
                      cambridgeDrafts.length === 1 ? "" : "s"
                    }`
                  : ""}
              </Text>
              {countsDiffer ? (
                <Text mt={1} fontSize="sm" color="orange.500" fontWeight="700">
                  Counts differ. Pairing is by row — merge, duplicate, or delete so each
                  question lines up with its model answer and mark scheme.
                </Text>
              ) : null}
            </Box>

            <SimpleGrid
              columns={{ base: 1, lg: 3 }}
              spacing={4}
              mb={-2}
              display={{ base: "none", lg: "grid" }}
            >
              <Text fontWeight="800" color="var(--text-primary)">
                Questions
              </Text>
              <Text fontWeight="800" color="var(--text-primary)">
                Model answers
              </Text>
              <Text fontWeight="800" color="var(--text-primary)">
                Mark schemes
              </Text>
            </SimpleGrid>

            <Stack spacing={4}>
              {Array.from({ length: pairCount }).map((_, idx) => {
                const question = draftQuestions[idx];
                const modelAnswer = modelAnswerDrafts[idx];
                const markScheme = cambridgeDrafts[idx];
                return (
                  <SimpleGrid
                    key={`pair-${idx}`}
                    columns={{ base: 1, lg: 3 }}
                    spacing={4}
                    alignItems="stretch"
                  >
                    {question
                      ? renderQuestionCard(question, idx)
                      : emptyPane("No question in this row — extra images here will not be imported.")}
                    {modelAnswer
                      ? renderSideCard(modelAnswer, idx, "model_answers")
                      : emptyPane(
                          "No model answer in this row. Split a model answer PDF, or leave empty."
                        )}
                    {markScheme
                      ? renderSideCard(markScheme, idx, "cambridge")
                      : emptyPane(
                          "No mark scheme in this row. Split a mark scheme PDF, or leave empty."
                        )}
                  </SimpleGrid>
                );
              })}
            </Stack>

            <Flex justify="flex-end" gap={4} pt={2}>
              <Button variant="ghost" fontWeight="800" onClick={closeFunc}>
                Cancel
              </Button>
              <Button
                bg={brandPalette.primary}
                color="white"
                borderRadius="lg"
                px={8}
                fontWeight="800"
                isLoading={isLoading}
                isDisabled={!draftQuestions.length}
                onClick={handleImport}
                _hover={{ bg: brandPalette.accentDark }}
              >
                Import {draftQuestions.length} question{draftQuestions.length === 1 ? "" : "s"}
              </Button>
            </Flex>
          </>
        ) : null}
      </Stack>

      <Modal
        isOpen={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
        size="4xl"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="var(--surface-raised)" maxW="min(90vw, 960px)">
          <ModalHeader color="var(--text-primary)">
            {previewItem?.list === "model_answers"
              ? "Model answer preview"
              : previewItem?.list === "cambridge"
                ? "Mark scheme preview"
                : previewItem?.question || "Question preview"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" flexDirection="column" alignItems="center" gap={4}>
            {previewItem ? (
              <>
                <Image
                  src={imageDataUrl(previewItem)}
                  alt={previewItem.question || "Preview"}
                  maxH="75vh"
                  maxW="100%"
                  objectFit="contain"
                />
                <Button
                  leftIcon={<RiCropLine />}
                  bg={brandPalette.primary}
                  color="white"
                  fontWeight="800"
                  onClick={() => {
                    setCropTarget({
                      list: previewItem.list || "questions",
                      item: previewItem,
                    });
                    setPreviewItem(null);
                  }}
                  _hover={{ bg: brandPalette.accentDark }}
                >
                  Crop this image
                </Button>
              </>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>

      <QuestionImageCropEditor
        isOpen={Boolean(cropTarget?.item)}
        title={
          cropTarget?.list === "model_answers"
            ? "Crop — model answer"
            : cropTarget?.list === "cambridge"
              ? "Crop — mark scheme"
              : cropTarget?.item
                ? `Crop — ${cropTarget.item.question}`
                : "Crop question image"
        }
        src={
          cropTarget?.item
            ? `data:${cropTarget.item.mime_type};base64,${
                cropTarget.item.original_image_base64 || cropTarget.item.image_base64
              }`
            : ""
        }
        mimeType={cropTarget?.item?.mime_type || "image/jpeg"}
        onApply={applyCrop}
        onClose={() => setCropTarget(null)}
      />
    </MainModel>
  );
};

export default BulkImportQuestions;
